import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { usePatient } from "@/context/PatientContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pill, Plus, PencilSimple, Trash, ArrowClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const empty = {
  name: "", kind: "prescription", dosage: "", strength: "", form: "tablet",
  color: "", shape: "", imprint: "", purpose: "",
  schedule: "", instructions: "", prescriber: "", pharmacy: "",
  refills_remaining: 0, quantity: 0, active: true,
};

export default function Medications() {
  const { activePatient } = usePatient();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("all");
  const [labelFor, setLabelFor] = useState(null);

  const load = useCallback(async () => {
    if (!activePatient) return;
    setLoading(true);
    try {
      const { data } = await api.get("/medications", { params: { patient_id: activePatient.id } });
      setItems(data);
    } finally { setLoading(false); }
  }, [activePatient]);
  useEffect(() => { load(); }, [load]);

  const startNew = (kind) => { setForm({ ...empty, kind: kind || "prescription" }); setEditingId(null); setOpen(true); };
  const startEdit = (m) => { setForm({ ...empty, ...m }); setEditingId(m.id); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, patient_id: activePatient.id };
      if (editingId) {
        await api.patch(`/medications/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/medications", payload);
        toast.success("Added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.name}?`)) return;
    await api.delete(`/medications/${m.id}`);
    setItems((xs) => xs.filter((x) => x.id !== m.id));
    toast.success("Deleted");
  };
  const refill = async (m) => {
    const n = Number(prompt("How many refills to add?", "3"));
    if (!n || Number.isNaN(n)) return;
    const { data } = await api.patch(`/medications/${m.id}`, {
      refills_remaining: (m.refills_remaining || 0) + n,
      last_refilled_at: new Date().toISOString(),
    });
    setItems((xs) => xs.map((x) => (x.id === m.id ? data : x)));
    toast.success("Refills updated");
  };

  const filtered = items.filter((m) => tab === "all" || m.kind === tab);

  if (!activePatient) return <div className="text-[#576A62]">Add a patient first.</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Medicines & prescriptions</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">Medications</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="medications-add-rx-btn" onClick={() => startNew("prescription")} className="inline-flex items-center gap-2 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-3 font-semibold">
            <Plus weight="bold" size={16}/> Add prescription
          </button>
          <button data-testid="medications-add-otc-btn" onClick={() => startNew("otc")} className="inline-flex items-center gap-2 rounded-full border border-[#2C5545] text-[#2C5545] hover:bg-[#EBE8E0] px-5 py-3 font-semibold">
            <Plus weight="bold" size={16}/> Add OTC
          </button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-[#EBE8E0]/50 p-1 h-auto">
          <TabsTrigger data-testid="med-tab-all" value="all" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 py-2">All</TabsTrigger>
          <TabsTrigger data-testid="med-tab-rx" value="prescription" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 py-2">Prescription</TabsTrigger>
          <TabsTrigger data-testid="med-tab-otc" value="otc" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 py-2">OTC</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-6">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-4">
              {[0,1,2,3].map((i) => <div key={i} className="h-40 rounded-2xl bg-[#EBE8E0]/50 animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#EBE8E0] p-12 text-center bg-white">
              <Pill size={56} weight="duotone" className="text-[#2C5545] mx-auto" />
              <div className="mt-4 font-heading text-2xl font-bold">No medications listed</div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filtered.map((m) => (
                <div key={m.id} data-testid={`medication-card-${m.id}`} className="bg-white border border-[#EBE8E0] rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-full bg-[#EBE8E0] flex items-center justify-center shrink-0">
                        <Pill size={22} weight="duotone" className="text-[#2C5545]" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-heading text-lg font-bold truncate">{m.name}</div>
                        <div className="text-sm text-[#576A62]">
                          {[m.strength || m.dosage, m.form].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </div>
                    <span className={`shrink-0 text-xs px-3 py-1 rounded-full ${m.kind === "prescription" ? "bg-[#2C5545]/10 text-[#2C5545]" : "bg-[#C86A51]/12 text-[#B05A43]"}`}>
                      {m.kind === "prescription" ? "Rx" : "OTC"}
                    </span>
                  </div>
                  {m.purpose && <div className="mt-3 text-sm text-[#576A62]">For: {m.purpose}</div>}
                  {m.schedule && <div className="mt-1 text-sm text-[#576A62]">Schedule: {m.schedule}</div>}
                  {m.kind === "prescription" && (
                    <div className={`mt-3 flex items-center gap-2 text-sm ${(m.refills_remaining || 0) <= 1 ? "text-[#B37927]" : "text-[#576A62]"}`}>
                      {(m.refills_remaining || 0) <= 1 && <Warning size={14} weight="duotone"/>}
                      Refills: <span className="font-semibold">{m.refills_remaining || 0}</span>
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setLabelFor(m)}
                      data-testid={`medication-label-${m.id}`}
                      className="rounded-full text-sm px-4 py-1.5 bg-[#2C5545] hover:bg-[#224235] text-white font-semibold"
                    >Easy-read label</button>
                    {m.kind === "prescription" && (
                      <button
                        onClick={() => refill(m)}
                        data-testid={`medication-refill-${m.id}`}
                        className="rounded-full text-sm px-3 py-1.5 border border-[#EBE8E0] hover:bg-[#EBE8E0]/60 inline-flex items-center gap-1"
                      ><ArrowClockwise size={14}/> Log refill</button>
                    )}
                    <button
                      onClick={() => startEdit(m)}
                      data-testid={`medication-edit-${m.id}`}
                      className="rounded-full text-sm px-3 py-1.5 border border-[#EBE8E0] hover:bg-[#EBE8E0]/60 inline-flex items-center gap-1"
                    ><PencilSimple size={14}/> Edit</button>
                    <button
                      onClick={() => remove(m)}
                      data-testid={`medication-delete-${m.id}`}
                      className="rounded-full text-sm px-3 py-1.5 border border-[#EBE8E0] text-[#C86A51] hover:bg-[#C86A51]/10 inline-flex items-center gap-1"
                    ><Trash size={14}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit medication" : "Add medication"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="med-form-name" placeholder="Lisinopril" />
              <SelectField label="Type" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} testid="med-form-kind" options={[
                { value: "prescription", label: "Prescription" }, { value: "otc", label: "Over-the-counter" }
              ]} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <TextField label="Strength" value={form.strength} onChange={(v) => setForm({ ...form, strength: v })} testid="med-form-strength" placeholder="10mg" />
              <SelectField label="Form" value={form.form} onChange={(v) => setForm({ ...form, form: v })} testid="med-form-form" options={[
                { value: "tablet", label: "Tablet" }, { value: "capsule", label: "Capsule" }, { value: "liquid", label: "Liquid" },
                { value: "injection", label: "Injection" }, { value: "patch", label: "Patch" }, { value: "cream", label: "Cream" }, { value: "other", label: "Other" },
              ]} />
              <TextField label="Dosage" value={form.dosage} onChange={(v) => setForm({ ...form, dosage: v })} testid="med-form-dosage" placeholder="1 pill" />
            </div>
            <TextField label="Purpose" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} testid="med-form-purpose" placeholder="Blood pressure" />
            <TextField label="Schedule" value={form.schedule} onChange={(v) => setForm({ ...form, schedule: v })} testid="med-form-schedule" placeholder="Once daily, morning" />
            <TextField label="Instructions" textarea value={form.instructions} onChange={(v) => setForm({ ...form, instructions: v })} testid="med-form-instructions" placeholder="Take with food" />
            <div className="grid grid-cols-3 gap-3">
              <TextField label="Color" value={form.color} onChange={(v) => setForm({ ...form, color: v })} testid="med-form-color" placeholder="White" />
              <TextField label="Shape" value={form.shape} onChange={(v) => setForm({ ...form, shape: v })} testid="med-form-shape" placeholder="Round" />
              <TextField label="Imprint" value={form.imprint} onChange={(v) => setForm({ ...form, imprint: v })} testid="med-form-imprint" placeholder="M L23" />
            </div>
            {form.kind === "prescription" && (
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Prescriber" value={form.prescriber} onChange={(v) => setForm({ ...form, prescriber: v })} testid="med-form-prescriber" />
                <TextField label="Pharmacy" value={form.pharmacy} onChange={(v) => setForm({ ...form, pharmacy: v })} testid="med-form-pharmacy" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Refills remaining" type="number" value={form.refills_remaining} onChange={(v) => setForm({ ...form, refills_remaining: v })} testid="med-form-refills" />
              <TextField label="Quantity" type="number" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} testid="med-form-quantity" />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]/60">Cancel</button>
              <button data-testid="med-form-save" type="submit" className="rounded-full px-5 py-2.5 bg-[#2C5545] hover:bg-[#224235] text-white font-semibold">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Easy-read Label dialog */}
      <Dialog open={!!labelFor} onOpenChange={(o) => !o && setLabelFor(null)}>
        <DialogContent className="max-w-xl rounded-2xl">
          {labelFor && (
            <div data-testid="medication-label-dialog">
              <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Easy-read label</div>
              <div className="med-label mt-2 p-6 pl-8 bg-[#FDFBF7]">
                <div className="font-heading text-4xl sm:text-5xl font-extrabold text-[#1A2520] leading-tight" data-testid="label-name">{labelFor.name}</div>
                <div className="mt-2 text-2xl font-semibold text-[#2C5545]" data-testid="label-strength">{[labelFor.strength, labelFor.form].filter(Boolean).join(" · ")}</div>
                <div className="mt-6 text-2xl text-[#1A2520] leading-snug" data-testid="label-schedule">{labelFor.schedule || labelFor.dosage}</div>
                {labelFor.instructions && <div className="mt-3 text-xl text-[#576A62] leading-snug">{labelFor.instructions}</div>}
                <div className="mt-6 grid grid-cols-3 gap-4 text-lg">
                  <LabelStat k="Color" v={labelFor.color} />
                  <LabelStat k="Shape" v={labelFor.shape} />
                  <LabelStat k="Imprint" v={labelFor.imprint} />
                </div>
                {labelFor.purpose && <div className="mt-6 text-lg"><span className="text-[#576A62]">For:</span> <span className="font-semibold">{labelFor.purpose}</span></div>}
                {labelFor.prescriber && <div className="mt-1 text-base text-[#576A62]">Prescribed by {labelFor.prescriber}</div>}
              </div>
              <div className="mt-4 text-right">
                <button onClick={() => window.print()} data-testid="label-print" className="rounded-full px-5 py-2.5 border border-[#2C5545] text-[#2C5545] hover:bg-[#EBE8E0]">Print</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LabelStat({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#576A62]">{k}</div>
      <div className="mt-1 font-semibold">{v || "—"}</div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder, required, testid, textarea }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{label}{required && <span className="text-[#C86A51]"> *</span>}</div>
      {textarea ? (
        <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          rows={3} className="w-full rounded-xl border border-[#EBE8E0] bg-white px-4 py-3 focus:ring-2 focus:ring-[#2C5545] outline-none" />
      ) : (
        <input data-testid={testid} type={type} required={required} value={value ?? ""}
          onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
          placeholder={placeholder} className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] outline-none" />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{label}</div>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-3">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
